import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { Load } from 'types/Load';
import { MaybeUndef } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import { CreateLoadPayload } from 'types/api/LoadPayload';
import captureException from 'utils/captureException';
import type { CreateLoadDateOnlyMetadata } from 'utils/dateOnlyFormValue';

// Extend with load metadata as needed
type CreateLoadMetadata = CreateLoadDateOnlyMetadata & {
  acknowledgeProdTMSOverride?: boolean;
};

export type CreateLoadRequest = {
  // Load building suggestion context
  suggestionId?: number;
  // If no suggestion, then threadId and email identifier (Drumkit id if available, else externalId)
  threadId?: string;
  emailId?: number;
  emailExternalId?: string;
  load: CreateLoadPayload;
  metadata?: CreateLoadMetadata;
};

export type CreateLoadResponse = {
  load: Load;
  message: string;
  errorLevel?: 'error' | 'warning'; // Error level for partial success scenarios (e.g., Revenova post-create update warnings)
};

// Normalize pallet counts to integers - pallet counts should never be floats
// Preserves null values (converts to null) to maintain explicit null semantics
function normalizePalletCounts(
  load: CreateLoadRequest['load']
): CreateLoadRequest['load'] {
  if (!load.specifications) {
    return load;
  }

  const normalizeValue = (value: MaybeUndef<number>): MaybeUndef<number> => {
    // Preserve null explicitly (don't convert to undefined)
    if (value === null) {
      return null;
    }
    // Convert undefined or invalid values to undefined (omit from JSON)
    if (value == null || !Number.isFinite(value)) {
      return undefined;
    }
    // Round finite numbers to integers
    return Math.round(value);
  };

  return {
    ...load,
    specifications: {
      ...load.specifications,
      totalInPalletCount: normalizeValue(
        load.specifications.totalInPalletCount
      ),
      totalOutPalletCount: normalizeValue(
        load.specifications.totalOutPalletCount
      ),
    },
  };
}

export async function createLoad(
  data: CreateLoadRequest
): Promise<Result<CreateLoadResponse, ApiError>> {
  try {
    // Normalize pallet counts to integers before sending
    // This prevents float values (e.g., 17.67) from being sent to the API
    const normalizedData: CreateLoadRequest = {
      ...data,
      load: normalizePalletCounts(data.load),
    };

    const response = await axios.post<CreateLoadResponse>(
      `/load`,
      normalizedData
    );

    return ok(response.data);
  } catch (error) {
    if (!isAxiosError(error)) {
      captureException(error, { functionName: 'createLoad' });
      return err({ message: 'Unable to create load.' });
    }

    const isValidationError =
      error.response?.status === 422 &&
      error.response?.data?.errorCode === 'load_validation_error';
    if (!isValidationError) {
      captureException(error, { functionName: 'createLoad' });
    }

    if (error.response?.status === 401) {
      throw error;
    }

    if (error.message === 'Extension context invalidated.') {
      throw error;
    }

    const data = error.response?.data as {
      message?: string;
      code?: string;
      errorCode?: string;
      load?: Load;
      level?: 'error' | 'warning';
      errorLevel?: 'error' | 'warning';
    };

    return err({
      message: data?.message || 'Unable to create load.',
      status: error.response?.status,
      response: data,
      load: data?.load,
      level: data?.level || data?.errorLevel,
    });
  }
}
